import { useEffect, useState } from "react";

export interface ShellDebugInfo {
  origin: string;
  href: string;
  displayMode: "browser" | "standalone" | "fullscreen" | "minimal-ui" | "unknown";
  fullscreenActive: boolean;
  fullscreenEnabled: boolean;
  navigatorStandalone: boolean;
  innerWidth: number;
  innerHeight: number;
  clientWidth: number;
  clientHeight: number;
  visualViewportWidth: number | null;
  visualViewportHeight: number | null;
  visualViewportOffsetTop: number | null;
  safeAreaInsetTopPx: number;
}

export function detectDisplayMode(): ShellDebugInfo["displayMode"] {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "unknown";
  }
  if (window.matchMedia("(display-mode: fullscreen)").matches) return "fullscreen";
  if (window.matchMedia("(display-mode: standalone)").matches) return "standalone";
  if (window.matchMedia("(display-mode: minimal-ui)").matches) return "minimal-ui";
  if (window.matchMedia("(display-mode: browser)").matches) return "browser";
  return "unknown";
}

function readSafeAreaInsetTopPx(): number {
  if (typeof document === "undefined") return 0;

  const probe = document.createElement("div");
  probe.style.position = "fixed";
  probe.style.top = "0";
  probe.style.left = "0";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  probe.style.paddingTop = "env(safe-area-inset-top, 0px)";
  document.body.appendChild(probe);

  const px = Number.parseFloat(window.getComputedStyle(probe).paddingTop);
  probe.remove();
  return Number.isFinite(px) ? px : 0;
}

function snapshotShellDebugInfo(): ShellDebugInfo {
  const viewport = window.visualViewport;
  return {
    origin: window.location.origin,
    href: window.location.href,
    displayMode: detectDisplayMode(),
    fullscreenActive: Boolean(document.fullscreenElement),
    fullscreenEnabled: document.fullscreenEnabled,
    navigatorStandalone: Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone),
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    clientWidth: document.documentElement.clientWidth,
    clientHeight: document.documentElement.clientHeight,
    visualViewportWidth: viewport ? Math.round(viewport.width * 100) / 100 : null,
    visualViewportHeight: viewport ? Math.round(viewport.height * 100) / 100 : null,
    visualViewportOffsetTop: viewport ? Math.round(viewport.offsetTop * 100) / 100 : null,
    safeAreaInsetTopPx: readSafeAreaInsetTopPx(),
  };
}

export function useShellDebug(enabled: boolean): ShellDebugInfo | null {
  const [info, setInfo] = useState<ShellDebugInfo | null>(null);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      setInfo(null);
      return;
    }

    const mediaQueries = [
      window.matchMedia("(display-mode: browser)"),
      window.matchMedia("(display-mode: standalone)"),
      window.matchMedia("(display-mode: fullscreen)"),
      window.matchMedia("(display-mode: minimal-ui)"),
    ];

    const update = () => {
      setInfo(snapshotShellDebugInfo());
    };

    update();

    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    document.addEventListener("fullscreenchange", update);
    for (const mq of mediaQueries) {
      mq.addEventListener("change", update);
    }

    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", update);
    viewport?.addEventListener("scroll", update);

    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      document.removeEventListener("fullscreenchange", update);
      for (const mq of mediaQueries) {
        mq.removeEventListener("change", update);
      }
      viewport?.removeEventListener("resize", update);
      viewport?.removeEventListener("scroll", update);
    };
  }, [enabled]);

  return info;
}